import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import PropTypes from 'prop-types';
import * as React from 'react';
import { Box, Flex } from 'rebass';
import styled from 'styled-components';

import Button from '@/components/Button/Button';
import CardContainer from '@/components/Card';
import ContextMenu from '@/components/ContextMenu/ContextMenu';
import DefaultInput from '@/components/DefaultInput/DefaultInput';
import DefaultTextarea from '@/components/DefaultInput/DefaultTextarea';
import Divider from '@/components/Divider';
import { Close, Trash } from '@/components/Icon';
import { CheckTask } from '@/components/Inputs';
import SpacerFooter from '@/components/Layout/SpacerFooter';
import Text from '@/components/Text';
import useMutationProject from '@/hooks/mutations/useMutationProject';
import useQueryCalendar from '@/hooks/queries/useQueryCalendar';
import { useQueryProject } from '@/hooks/queries/useQueryProject';
import DocumentUpload from '@/widgets/Projekte/DocumentUpload';

import Due from './Due';

const Details = styled(Box)`
  position: fixed;
  top: 65px;
  right: 0;
  height: calc(100% - 65px);
  width: 50vw;
  z-index: 2;
  overflow-y: scroll;
  overflow-x: hidden;
  @media (max-width: 1024px) {
    overflow-y: inherit;
    width: 100vw;
  }
`;

const CardContainerFullHeight = styled(CardContainer)`
  min-height: calc(100vh - 70px);
`;

const DetailContentSubTask = ({ close, subitem: { pid, tid, sid } = {} }) => {
  const { data, isSuccess: isSuccessQueryProject } = useQueryProject(pid);
  const { check, uncheck, rename, renameNotes, renameDescription, deleteTask } = useMutationProject(pid, tid, sid);
  const { data: res, isSuccess } = useQueryCalendar(pid);
  const { t: h } = useTranslation('page-projekte-erstellen');

  const subitem = React.useMemo(
    () => data?.task?.find?.((task) => task.id === tid)?.subtask?.find?.((subtask) => subtask.id === sid),
    [data?.task, sid, tid]
  );

  const appointment = React.useMemo(() => {
    if (!isSuccess) return {};
    return res.data.reduce((acc, curr) => (curr.fk_subtask !== sid ? acc : curr), {});
  }, [isSuccess, res?.data, sid]);

  if (!isSuccess && !isSuccessQueryProject) return <div />;

  const handleCheck = async () => {
    if (subitem?.done) {
      await uncheck();
    } else {
      await check();
    }
  };

  const handleDeleteTask = async () => {
    await deleteTask();
    close();
  };

  return (
    <Details>
      <CardContainerFullHeight
        borderLeft={['0', '0', '1']}
        borderBottom={['0', '0', '1']}
        borderLeftColor={['', '', '#ebebeb']}
        borderBottomColor={['', '', '#ebebeb']}
        ml={['0', '0', '3px']}
        mb={['0', '0', '3px']}
        px={['0', '0', '0']}
        py={['12px', '12px', '14px']}
        sx={{ overflowY: 'auto', paddingTop: '14px' }}
      >
        <Flex alignItems="center" flexDirection="row" justifyContent="space-between" px={['15px', '30px', '31.5px']}>
          <Flex>
            <Flex
              onClick={close}
              alignItems="center"
              justifyContent="center"
              sx={{
                background: '#fff',
                border: '1px solid #ebebeb',
                borderRadius: '50%',
                cursor: 'pointer',
                height: '2.7rem',
                width: '2.7rem',
                zIndex: 1,
                mr: '1rem',
              }}
            >
              <Close />
            </Flex>
            <Box sx={{ position: 'relative' }}>
              <Due id={subitem?.id} projectId={pid} appointment={appointment} title={subitem?.name} type="subtask" />
            </Box>
          </Flex>
          <Flex justifyContent="flex-end">
            <ContextMenu
              options={[
                {
                  title: 'Unteraufgabe löschen',
                  icon: <Trash />,
                  action: handleDeleteTask,
                },
              ]}
            />
          </Flex>
        </Flex>
        <Box>
          <Divider />
        </Box>
        <Flex
          flexDirection={['column', 'column', 'column']}
          width={['100%']}
          pl={['15px', '30px', '30px']}
          pr={['30px', '30px', '30px']}
        >
          <Flex mb={['1.177rem', '1.177rem', '1.177rem']} flexDirection={['row', 'row', 'row']}>
            <Flex mt={['.6rem']} width={['1.875rem']}>
              <CheckTask width="17px" height="17px" checked={subitem?.done} onChange={handleCheck} />
            </Flex>
            <Flex width={['100%']}>
              <DefaultInput
                autoFocus={subitem?.name === 'Unteraufgabe'}
                initalValue={subitem?.name}
                id={subitem?.id}
                name="name"
                type="text"
                handleChangeInput={rename}
                variant={subitem?.done ? 'inputSubTaskDel' : 'inputSubTask'}
                placeholder="Aufgabenname eingeben"
              />
            </Flex>
          </Flex>
          <Flex flexDirection={['row', 'row', 'row']}>
            <Flex width={['1.875rem']} />
            <Box width={['100%']}>
              <Text mb={['0.219rem', '0.219rem', '0.219rem']} pl={['0.4rem', '0.4rem', '0.4rem']} variant="capsTitle">
                {h('subtaskDetails.titleDescription')}
              </Text>
              <DefaultTextarea
                initalValue={subitem?.description}
                id="description"
                name="description"
                resize="none"
                type="textarea"
                handleChangeInput={renameDescription}
                placeholder="Beschreibung hinzufügen"
              />
              {subitem?.link?.url ? (
                <Link href={subitem?.link?.url}>
                  <a title={subitem?.link?.caption}>
                    <Button mt={['1.25rem', '1.25rem', '1.898rem']} mb={['1rem']} ml={['8px', '8px', '8px']}>
                      {subitem?.link?.caption}
                    </Button>
                  </a>
                </Link>
              ) : null}
            </Box>
          </Flex>
        </Flex>
        <Box>
          <Divider />
        </Box>
        <Box pl={['15px', '30px', '30px']} pr={['30px', '30px', '30px']}>
          <Box pl={['2.275rem']} pt={['4px']}>
            <Text variant="taskTextLight">Notizen</Text>
          </Box>
          <Box pl={['1.875rem']} pt={['4px']}>
            <DefaultTextarea
              initalValue={subitem?.notes}
              id="notes"
              maxLength="5000"
              name="notes"
              type="textarea"
              handleChangeInput={renameNotes}
              placeholder="Notizen hinzufügen"
            />
          </Box>
        </Box>
        <Box>
          <Divider />
        </Box>
        <DocumentUpload taskId={tid} sid={sid} />
        <SpacerFooter />
      </CardContainerFullHeight>
    </Details>
  );
};

DetailContentSubTask.propTypes = {
  close: PropTypes.string.isRequired,
  subitem: PropTypes.shape({
    description: PropTypes.string,
    done: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    id_task: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    notes: PropTypes.string.isRequired,
  }).isRequired,
};

export default DetailContentSubTask;
